import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Shield, Clock, Award, Users } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";

/* Service overview highlights */
const SERVICE_HIGHLIGHTS = [
  {
    icon: Shield,
    title: "Fully Insured",
    desc: "$20M public liability coverage across all projects"
  },
  {
    icon: Award,
    title: "Certified Team",
    desc: "IRATA certified, WHS compliant and fully trained personnel"
  },
  {
    icon: Clock,
    title: "Flexible Scheduling",
    desc: "24/7 availability to meet construction and operational timelines"
  },
  {
    icon: Users,
    title: "Dedicated Support",
    desc: "Direct communication with your assigned project supervisor"
  }
];

/* Professional cleaning service images */
const SERVICE_IMAGES = {
  builders: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/e1jni46i_11.jpg",
  industrial: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/cknabjp4_Untitled%20%281200%20x%20900%20px%29.png",
  pressureWashing: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/qoxikyon_1ee8394274a8f8e7fcf3c0fa7820a3fe.jpg",
  strata: "https://images.unsplash.com/photo-1758193017781-e3aee6c3e359?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  window: "https://images.unsplash.com/photo-1761689502577-0013be84f1bf?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  deepClean: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/syct2a09_503f4b83a8c27ccd9c5f9f17542b912e.jpg",
  officeCleaning: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/efcgibnr_office.jpg",
  ndisCleaning: "https://images.pexels.com/photos/4239035/pexels-photo-4239035.jpeg?auto=compress&cs=tinysrgb&w=1200",
};

/* Hero background image - HD 4K window cleaners on modern building */
const HERO_BG = "https://images.unsplash.com/photo-1773924624330-7e7cc51d8838?w=2400&q=90";

const SERVICES = [
  {
    id: "01",
    title: "Builder's Cleaning",
    subtitle: "Construction & Handover",
    image: SERVICE_IMAGES.builders,
    description: "Comprehensive site cleaning from pre-caulking through to final handover.",
    features: ["Pre-Caulking", "Builder's Clean", "Final / Sparkle Clean", "Windows & Facade"],
  },
  {
    id: "02",
    title: "Industrial Cleaning",
    subtitle: "Warehouses & Facilities",
    image: SERVICE_IMAGES.industrial,
    description: "Specialised cleaning for industrial sites and large-scale environments.",
    features: ["Floor Scrubbing", "High-Pressure Cleaning", "Cladding & Facade", "Loading Docks", "Deep Cleaning", "Plant & Equipment Cleaning"],
  },
  {
    id: "03",
    title: "Facilities Management",
    subtitle: "Apartments & Complexes",
    image: SERVICE_IMAGES.strata,
    description: "Ongoing cleaning and maintenance for shared residential environments.",
    features: ["Common Areas", "Car Parks", "Bin Rooms", "Amenities"],
  },
  {
    id: "04",
    title: "Office Cleaning",
    subtitle: "Commercial Workspaces",
    image: SERVICE_IMAGES.officeCleaning,
    description: "Maintaining clean, professional and healthy office environments.",
    features: ["Daily Cleaning", "Workstations & Surfaces", "Kitchens & Break Areas", "Restrooms", "Carpet Care"],
  },
  {
    id: "05",
    title: "Deep Cleaning",
    subtitle: "Maintenance Services",
    image: SERVICE_IMAGES.deepClean,
    description: "Scheduled and intensive cleaning for high-traffic environments.",
    features: ["Routine Maintenance", "Intensive Cleans", "Commercial & Multi-Site"],
  },
  {
    id: "06",
    title: "Window & Facade Cleaning",
    subtitle: "High-Rise Specialists",
    image: SERVICE_IMAGES.window,
    description: "Rope access solutions for buildings of any scale.",
    features: ["Rope Access", "Facade Washing", "High-Rise Cleaning", "IRATA Certified"],
  },
  {
    id: "07",
    title: "Pressure Cleaning",
    subtitle: "External Surfaces",
    image: SERVICE_IMAGES.pressureWashing,
    description: "High-pressure cleaning for external and hard surface areas.",
    features: ["Driveways", "Car Parks", "Building Exteriors", "Graffiti Removal"],
  },
];

/* Service Card Component */
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isEven = index % 2 === 0;
  
  // Create URL-friendly slug from service title
  const serviceSlug = service.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <ScrollReveal>
      <div 
        id={serviceSlug}
        className={`grid grid-cols-1 lg:grid-cols-2 ${isEven ? '' : 'lg:grid-flow-dense'} scroll-mt-24`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={`service-section-${index}`}
      >
        {/* Image Side - Fixed aspect ratio */}
        <div className={`relative overflow-hidden aspect-square lg:aspect-auto lg:min-h-[600px] ${isEven ? '' : 'lg:col-start-2'}`}>
          <motion.img
            src={service.image}
            alt={service.title}
            className="absolute inset-0 w-full h-full object-cover"
            animate={{ scale: isHovered ? 1.05 : 1 }}
            transition={{ duration: 0.6 }}
          />
          <div className="absolute inset-0 bg-black/20" />
          {/* Large number overlay */}
          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10">
            <span className="text-7xl md:text-9xl font-semibold text-white/10" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {service.id}
            </span>
          </div>
        </div>

        {/* Content Side */}
        <div className={`bg-white p-8 md:p-16 flex flex-col justify-center min-h-[400px] lg:min-h-[600px] ${isEven ? '' : 'lg:col-start-1'}`}>
          <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.25em] uppercase mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {service.subtitle}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-neutral-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {service.title}
          </h2>
          <p className="text-neutral-600 text-base md:text-lg leading-relaxed mb-8 max-w-md" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {service.description}
          </p>
          
          {/* Features */}
          <div className="flex flex-wrap gap-2 mb-8">
            {service.features.map((feature) => (
              <span 
                key={feature}
                className="px-3 py-1.5 text-xs font-medium text-neutral-600 border border-neutral-200 rounded-full"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
};

export default function ServicesPage() {
  const location = useLocation();

  // Scroll to hash anchor on page load or hash change
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]); // Only re-run when hash changes

  return (
    <div data-testid="services-page" className="bg-white">
      {/* Hero - Job site photo with overlay */}
      <section className="pt-32 pb-20 md:pt-44 md:pb-28 relative overflow-hidden min-h-[60vh] md:min-h-[50vh]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/jlrrkqyv_0037c475-2bf3-4809-93aa-2fb17c18c44d.png" 
            alt="" 
            className="w-full h-full object-cover object-[center_40%]"
          />
          {/* Dark overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/70 to-[#1a1a1a]/50" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.3em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              What We Do
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] tracking-tight max-w-4xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Services
              <span className="text-[#B8D86B]">.</span>
            </h1>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-xl leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Comprehensive cleaning solutions for commercial, industrial and residential properties across Melbourne.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-28 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - Overview Text */}
            <ScrollReveal>
              <div>
                <p className="text-[#B8D86B] text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Why Choose PKC
                </p>
                <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Built for Performance. Trusted for Delivery.
                </h2>
                <p className="text-neutral-600 leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Since 2022, PKC Property Services has delivered high-standard cleaning across Melbourne's construction, commercial and industrial sectors.
                </p>
                <p className="text-neutral-600 leading-relaxed mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  We work closely with builders, developers and property managers to deliver reliable, scalable cleaning solutions, from construction through to ongoing maintenance. Every engagement is tailored to your site, schedule and operational requirements.
                </p>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#B8D86B] text-neutral-900 px-8 py-4 text-sm font-semibold hover:bg-[#A3C65A] transition-colors duration-300"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Speak with Our Team
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </ScrollReveal>

            {/* Right - Highlights Grid */}
            <div>
              <p className="text-[#B8D86B] text-xs font-medium tracking-[0.25em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Why Clients Choose PKC
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SERVICE_HIGHLIGHTS.map((item, idx) => (
                  <ScrollReveal key={item.title} delay={idx * 0.1}>
                    <div className="bg-white p-6 hover:shadow-md transition-shadow duration-300">
                      <item.icon className="w-8 h-8 text-[#B8D86B] mb-4" strokeWidth={1.5} />
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {item.title}
                      </h3>
                      <p className="text-neutral-500 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {item.desc}
                      </p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Header */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-[#B8D86B] text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Services
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Services Grid */}
      <section>
        {SERVICES.map((service, idx) => (
          <ServiceCard key={service.id} service={service} index={idx} />
        ))}
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ready to get started?
            </h2>
            <p className="text-neutral-600 text-lg mb-10 max-w-lg mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Request a tailored quote for your property. Our team will respond within 24 hours.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-btn"
              className="inline-flex items-center gap-2 bg-[#B8D86B] text-neutral-900 px-10 py-4 text-sm font-semibold hover:bg-[#A3C65A] transition-colors duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Request a Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
